import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { ApiResponse, Tenant } from '@cartiq/shared';
import { API_BASE } from '../config';
import { authActions, type AuthUser } from '../stores/authStore';

export const http: AxiosInstance = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // send httpOnly refresh cookie
  headers: { 'Content-Type': 'application/json' },
});

// ---- Request interceptor: attach Bearer token ----
http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = authActions.getToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

// ---- Response interceptor: 401 -> refresh once -> retry ----
interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

let refreshPromise: Promise<string | null> | null = null;

interface RefreshResult {
  accessToken: string;
  user?: AuthUser;
  tenant?: Tenant;
}

async function postRefresh(): Promise<string | null> {
  try {
    const res = await axios.post<ApiResponse<RefreshResult>>(
      `${API_BASE}/auth/refresh`,
      {},
      { withCredentials: true },
    );
    const data = res.data?.data;
    const token = data?.accessToken ?? null;
    // `/auth/refresh` returns the full session, so a cold-load restore is done
    // in this one call. Fall back to a token-only update if it's ever absent.
    if (token && data?.user && data?.tenant) {
      authActions.setSession({ accessToken: token, user: data.user, tenant: data.tenant });
    } else {
      authActions.setToken(token); // broadcasts the new token to other tabs
    }
    return token;
  } catch {
    authActions.logout();
    return null;
  }
}

/**
 * Refresh the access token, coordinating across browser tabs.
 *
 * The refresh cookie is single-use and rotated server-side, so two tabs hitting
 * /auth/refresh with the same cookie race — the loser presents a revoked token
 * and gets logged out. We serialize refreshes across tabs with the Web Locks
 * API and short-circuit if a peer already produced a newer token (its value
 * arrives over the auth BroadcastChannel and lands in the store).
 *
 * @param failedToken the token that was attached to the request that 401'd, so
 *   we can tell a stale same-tab token apart from one a peer already refreshed.
 */
async function refreshToken(failedToken: string | null): Promise<string | null> {
  const run = async (): Promise<string | null> => {
    const current = authActions.getToken();
    // A peer (or an earlier waiter on this lock) already refreshed.
    if (current && current !== failedToken) return current;
    return postRefresh();
  };

  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (locks?.request) {
    try {
      return await locks.request('cartiq:auth-refresh', run);
    } catch {
      return run();
    }
  }
  return run();
}

/**
 * Coordinated refresh entry point. Dedups concurrent callers within a tab via
 * the `refreshPromise` singleton (the 401 interceptor and the boot-time session
 * bootstrap share it), and coordinates across tabs inside `refreshToken`.
 */
function coordinatedRefresh(failedToken: string | null): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = refreshToken(failedToken).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/**
 * Mint an access token from the httpOnly refresh cookie before the first
 * protected request. Called on app boot when a tab has the "was authed" hint
 * but no in-memory token, so `/auth/me` carries a Bearer instead of eating a
 * 401-then-retry. Returns null if there's no valid session to restore.
 */
export function bootstrapAuth(): Promise<string | null> {
  const existing = authActions.getToken();
  if (existing) return Promise.resolve(existing);
  return coordinatedRefresh(null);
}

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse>) => {
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    // Only skip the refresh-and-retry for endpoints where it makes no sense or
    // would recurse. `/auth/me` must stay retriable — it's how the session is
    // restored from the refresh cookie after a page reload.
    const url = original?.url ?? '';
    const isNonRetriableAuthCall =
      url.includes('/auth/refresh') ||
      url.includes('/auth/login') ||
      url.includes('/auth/logout');

    if (status === 401 && original && !original._retried && !isNonRetriableAuthCall) {
      original._retried = true;
      // The token that was attached to the failed request (if any), so the
      // refresh can distinguish a stale same-tab token from a peer's newer one.
      const authHeader = original.headers?.get?.('Authorization');
      const failedToken =
        typeof authHeader === 'string' ? authHeader.replace(/^Bearer\s+/i, '') : null;
      const token = await coordinatedRefresh(failedToken);
      if (token) {
        original.headers.set('Authorization', `Bearer ${token}`);
        return http(original);
      }
      // Refresh failed: redirect to login.
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.assign('/login');
      }
    }
    return Promise.reject(error);
  },
);

/** Unwrap the {success, data, error} envelope, throwing a friendly error. */
function unwrap<T>(payload: ApiResponse<T>): T {
  if (!payload.success || payload.data === undefined) {
    throw new ApiError(payload.error?.message ?? 'Request failed', payload.error?.code);
  }
  return payload.data;
}

export class ApiError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
  }
}

export function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as ApiResponse | undefined;
    return data?.error?.message ?? err.message ?? 'Something went wrong';
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}

// ---- Typed request helpers that unwrap the envelope ----
export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const res = await http.get<ApiResponse<T>>(url, config);
  return unwrap(res.data);
}

export async function apiPost<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await http.post<ApiResponse<T>>(url, body, config);
  return unwrap(res.data);
}

export async function apiPut<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await http.put<ApiResponse<T>>(url, body, config);
  return unwrap(res.data);
}

export async function apiPatch<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await http.patch<ApiResponse<T>>(url, body, config);
  return unwrap(res.data);
}

export async function apiDelete<T = void>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<T> {
  const res = await http.delete<ApiResponse<T>>(url, config);
  return unwrap(res.data);
}
